import React from 'react';

import { useMailboxComposerEnabled } from './use-mailbox-composer-enabled';
import { client } from '~/api/client';
import {
  CaraWorkspaceArtifactType,
  type CaraWorkspaceFieldsFragment,
  GetCaraWorkspaceArtifactDocument,
  type GetCaraWorkspaceArtifactQuery,
  type GetCaraWorkspaceArtifactQueryVariables,
  GetCaraWorkspaceEmailArtifactDocument,
  type GetCaraWorkspaceEmailArtifactQuery,
  type GetCaraWorkspaceEmailArtifactQueryVariables
} from '~/api/graphql';
import type { SaveStatus } from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import {
  applyWorkspaceTickToArtifact,
  withEmailBody,
  type FocusedArtifact
} from '~/components/workspaces/lib/email-artifact-body';
import { newestArtifactVersion } from '~/components/workspaces/lib/artifact';
import { openedDocumentVersion } from '~/components/documents/document-versions';
import { DISCARD_CHANGES_PROMPT } from '~/components/workspaces/lib/template-form-editor';

type Artifact = FocusedArtifact;

/**
 * Owns the right-rail artifact focus dialog: open/close state, the currently focused
 * artifact, an in-flight refetch for DocumentHtml artifacts (so the editor always has
 * up-to-date content), and a save-status guard that blocks switching artifacts while
 * the editor is mid-save.
 *
 * Returns:
 *  - `requestOpenArtifact`: guarded entrypoint - silently no-ops if a save is pending.
 *  - `openArtifactFocusDialogUnchecked`: bypasses the save guard (used by the URL hook
 *    on initial load when no editor is mounted yet).
 *  - `onSaveStatusChange`: bound directly to the HtmlDocumentFocus editor.
 *  - `onUnsavedChangesChange`: bound to the ACORD form editor; while it reports
 *    unsaved edits, `requestOpenArtifact` asks before opening a different artifact.
 */
export function useArtifactFocus(workspace: CaraWorkspaceFieldsFragment) {
  const mailboxEnabled = useMailboxComposerEnabled();
  const [showArtifactFocusDialog, setShowArtifactFocusDialog] = React.useState(false);
  const [currentArtifact, setCurrentArtifact] = React.useState<Artifact | null>(null);
  const artifactFetchTargetRef = React.useRef<{ id: string; version: number } | null>(null);
  const editorSaveStatusRef = React.useRef<SaveStatus>('idle');
  const unsavedChangesRef = React.useRef(false);
  const currentArtifactRef = React.useRef(currentArtifact);
  currentArtifactRef.current = currentArtifact;
  // A version a mutation returned can reach this hook before the workspace update that carries
  // it, and the panel closes on a version the workspace does not hold, so it waits for it.
  const awaitedVersionRef = React.useRef<{ id: string; version: number } | null>(null);
  // Blocks composer saves until the named version's body and contentRevision land.
  const [emailBodyHydrating, setEmailBodyHydrating] = React.useState(false);

  const workspaceId = workspace?.id;
  const workspaceRef = React.useRef(workspace);
  workspaceRef.current = workspace;

  // When opening the focus dialog for a DocumentHtml artifact, refetch it so we always
  // have document content (avoids stale/missing content when no network request was sent).
  // EMAIL versions whose body the workspace tick emptied get the same treatment via
  // GetCaraWorkspaceEmailArtifact so opening an older revision still shows that body.
  const openArtifactFocusDialogUnchecked = React.useCallback(
    (artifact: Artifact) => {
      // URL deep links and other artifact entrypoints must respect the same
      // gate as the email card. Only the save guard is bypassed here.
      if (artifact.type === CaraWorkspaceArtifactType.Email && !mailboxEnabled) return;
      // No version resolution here: this is also the URL hook's entrypoint, and
      // a deep link names the version it means (see requestOpenArtifact for the
      // card rule).
      setShowArtifactFocusDialog(true);
      setCurrentArtifact(artifact);
      setEmailBodyHydrating(false);
      if (!workspaceId || !artifact.id) return;

      const targetId = artifact.id;
      const targetVersion = artifact.version;
      artifactFetchTargetRef.current = { id: targetId, version: targetVersion };

      if (artifact.type === CaraWorkspaceArtifactType.DocumentHtml) {
        client
          .query<GetCaraWorkspaceArtifactQuery, GetCaraWorkspaceArtifactQueryVariables>({
            query: GetCaraWorkspaceArtifactDocument,
            variables: { workspaceId, artifactId: artifact.id }
          })
          .then((res) => {
            const summary = res.data?.caraWorkspaceArtifact;
            if (!summary) return;
            const versionRow = summary.versions.find((v) => v.version === targetVersion);
            if (!versionRow) return;
            if (
              artifactFetchTargetRef.current?.id !== targetId ||
              artifactFetchTargetRef.current?.version !== targetVersion
            ) {
              return;
            }
            const flatArtifact = {
              ...versionRow,
              id: summary.id
            } as Artifact;
            setCurrentArtifact(flatArtifact);
          })
          .finally(() => {
            if (
              artifactFetchTargetRef.current?.id === targetId &&
              artifactFetchTargetRef.current?.version === targetVersion
            ) {
              artifactFetchTargetRef.current = null;
            }
          });
        return;
      }

      // Always network-only: the watch cache holds emptied superseded bodies and
      // fingerprints hashed from them. A tool-call seed may fill the editor, but
      // only this response carries the stored body and contentRevision.
      if (artifact.type === CaraWorkspaceArtifactType.Email) {
        setEmailBodyHydrating(true);
        client
          .query<GetCaraWorkspaceEmailArtifactQuery, GetCaraWorkspaceEmailArtifactQueryVariables>({
            query: GetCaraWorkspaceEmailArtifactDocument,
            variables: { workspaceId, artifactId: artifact.id },
            fetchPolicy: 'network-only'
          })
          .then((res) => {
            const summary = res.data?.caraWorkspaceArtifact;
            if (!summary) return;
            const versionRow = summary.versions.find((v) => v.version === targetVersion);
            const bodyHtml = versionRow?.email?.bodyHtml;
            if (!versionRow || bodyHtml == null) return;
            if (
              artifactFetchTargetRef.current?.id !== targetId ||
              artifactFetchTargetRef.current?.version !== targetVersion
            ) {
              return;
            }
            const contentRevision = versionRow.email?.contentRevision;
            setCurrentArtifact((current) => {
              if (!current || current.id !== targetId || current.version !== targetVersion) {
                return current;
              }
              return withEmailBody(current, bodyHtml, contentRevision);
            });
          })
          .finally(() => {
            if (
              artifactFetchTargetRef.current?.id === targetId &&
              artifactFetchTargetRef.current?.version === targetVersion
            ) {
              artifactFetchTargetRef.current = null;
            }
            setEmailBodyHydrating(false);
          });
      }
    },
    [workspaceId, mailboxEnabled]
  );

  // Save-guarded version of openArtifactFocusDialog. Used by the conversation card
  // clicks; if the editor is mid-save we silently skip to avoid clobbering the in-flight
  // write.
  const requestOpenArtifact = React.useCallback(
    (artifact: Artifact) => {
      if (editorSaveStatusRef.current === 'saving') return;
      // The form editor keeps typing local until Save, so moving the dialog to
      // another artifact would discard it. A card for the same form is safe:
      // the editor holds the version it was typed into while it has edits.
      if (
        unsavedChangesRef.current &&
        currentArtifactRef.current?.id !== artifact.id &&
        !window.confirm(DISCARD_CHANGES_PROMPT)
      ) {
        return;
      }
      // A card records one version but opens the one a reader wants now: an
      // email's newest draft, since the server refuses to save a superseded
      // composition, and a document's openedDocumentVersion. Clicking through
      // while the composer is open moves it to the newer version, which
      // remounts the editor and drops an unsaved edit -- one that could not
      // have been saved against its version either. A deep link means a
      // specific version, so it goes through the unchecked entrypoint.
      const { id } = artifact;
      if (artifact.type === CaraWorkspaceArtifactType.Email) {
        artifact = newestArtifactVersion(workspaceRef.current.artifacts, id) ?? artifact;
      } else if (artifact.type === CaraWorkspaceArtifactType.DocumentHtml) {
        artifact = openedDocumentVersion(
          workspaceRef.current.artifacts.filter((each) => each.id === id),
          artifact
        );
      }
      openArtifactFocusDialogUnchecked(artifact);
    },
    [openArtifactFocusDialogUnchecked]
  );

  const closeArtifactFocusDialog = React.useCallback(() => {
    setShowArtifactFocusDialog(false);
    setEmailBodyHydrating(false);
  }, []);

  const onSaveStatusChange = React.useCallback((status: SaveStatus) => {
    editorSaveStatusRef.current = status;
  }, []);

  const onUnsavedChangesChange = React.useCallback((unsaved: boolean) => {
    unsavedChangesRef.current = unsaved;
  }, []);

  const showArtifactVersion = React.useCallback((artifact: Artifact) => {
    const arrived = workspaceRef.current.artifacts.some(
      (a) => a.id === artifact.id && a.version === artifact.version
    );
    awaitedVersionRef.current = arrived ? null : { id: artifact.id, version: artifact.version };
    if (arrived) setCurrentArtifact(artifact);
  }, []);

  // Sync currentArtifact with workspace updates (e.g., after mutations). Do not
  // depend on currentArtifact: applying a tick that carries a fetched body
  // allocates a new object, and listing it here would re-enter forever once an
  // older email revision has been opened and hydrated.
  React.useEffect(() => {
    const awaited = awaitedVersionRef.current;
    const arrived =
      awaited &&
      workspace.artifacts.find((a) => a.id === awaited.id && a.version === awaited.version);
    if (arrived) awaitedVersionRef.current = null;
    setCurrentArtifact((current) => {
      if (!current || !workspace) return current;
      if (arrived && arrived.id === current.id) return arrived;
      const updatedArtifact = workspace.artifacts.find(
        (a) => a.id === current.id && a.version === current.version
      );
      if (!updatedArtifact) return current;
      return applyWorkspaceTickToArtifact(current, updatedArtifact);
    });
  }, [workspace.updatedAt, workspace.artifacts]);

  return {
    showArtifactFocusDialog,
    currentArtifact,
    setCurrentArtifact,
    emailBodyHydrating,
    openArtifactFocusDialogUnchecked,
    requestOpenArtifact,
    closeArtifactFocusDialog,
    onSaveStatusChange,
    onUnsavedChangesChange,
    showArtifactVersion
  };
}
